import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Plus, Edit, Trash2, Loader2 } from 'lucide-react';

const SERVICE_CATEGORIES = [
  { value: 'infrastructure', label: 'Infrastructure (hosting, DB)' },
  { value: 'messaging', label: 'Messaging (Twilio, SendGrid)' },
  { value: 'storage', label: 'Storage (S3, R2, etc.)' },
  { value: 'integration', label: '3rd-party API (fal.ai, etc.)' },
  { value: 'other', label: 'Other' },
];

const UNITS = [
  { value: 'month', label: 'per month' },
  { value: 'request', label: 'per request' },
  { value: 'gb', label: 'per GB' },
  { value: 'message', label: 'per message' },
  { value: 'minute', label: 'per minute' },
];

/**
 * Service-costs sub-section of Admin → Integrations tab.
 * Extracted from the 584-line monolith in iter123.
 */
export const ServiceCostsSection = ({
  services, totalMonthly, showDialog, setShowDialog,
  form, setForm, editing, saving, onCreate, onEdit, onSave, onDelete,
}) => (
  <div className="space-y-4">
    <Card>
      <CardHeader className="flex-row items-center justify-between">
        <div>
          <CardTitle className="text-base">Connected Services & Recurring Costs</CardTitle>
          <CardDescription>
            Total active monthly recurring: <strong className="text-emerald-700">${totalMonthly.toFixed(2)}</strong>
            . This is amortized across users and added to plan pricing.
          </CardDescription>
        </div>
        <Button onClick={onCreate} className="gap-1" data-testid="add-service-cost-btn">
          <Plus className="w-4 h-4" /> Add Service
        </Button>
      </CardHeader>
      <CardContent>
        <div className="border rounded-lg overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="text-left px-3 py-2 font-medium">Service</th>
                <th className="text-left px-3 py-2 font-medium">Category</th>
                <th className="text-right px-3 py-2 font-medium">Cost</th>
                <th className="text-left px-3 py-2 font-medium">Unit</th>
                <th className="text-center px-3 py-2 font-medium">Active</th>
                <th className="text-right px-3 py-2 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {services.length === 0 ? (
                <tr><td colSpan={6} className="p-8 text-center text-slate-400">No services added yet. Click &quot;Add Service&quot; to start.</td></tr>
              ) : services.map((s) => (
                <tr key={s.service_cost_id} className="border-b last:border-b-0 hover:bg-slate-50">
                  <td className="px-3 py-2">
                    <div className="font-medium text-slate-900">{s.name}</div>
                    {s.notes && <div className="text-xs text-slate-500 mt-0.5">{s.notes}</div>}
                  </td>
                  <td className="px-3 py-2 text-slate-600 capitalize">{s.category}</td>
                  <td className="px-3 py-2 text-right font-mono">${(s.monthly_cost || 0).toFixed(2)}</td>
                  <td className="px-3 py-2 text-slate-600 text-xs">{s.unit}</td>
                  <td className="px-3 py-2 text-center">
                    {s.is_active ? <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">Active</Badge> : <Badge variant="outline">Off</Badge>}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Button size="sm" variant="ghost" onClick={() => onEdit(s)} className="h-7"><Edit className="w-3 h-3" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => onDelete(s)} className="h-7 text-red-600 hover:bg-red-50"><Trash2 className="w-3 h-3" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>

    {/* Service Cost Dialog */}
    <Dialog open={showDialog} onOpenChange={setShowDialog}>
      <DialogContent className="bg-white max-w-lg">
        <DialogHeader>
          <DialogTitle>{editing ? 'Edit Service Cost' : 'Add Service Cost'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label>Service Name *</Label>
            <Input data-testid="service-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g., Twilio SMS" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{SERVICE_CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Unit</Label>
              <Select value={form.unit} onValueChange={(v) => setForm({ ...form, unit: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{UNITS.map((u) => <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Cost (USD)</Label>
            <Input data-testid="service-cost" type="number" step="0.01" value={form.monthly_cost} onChange={(e) => setForm({ ...form, monthly_cost: e.target.value })} />
          </div>
          <div>
            <Label>Notes (optional)</Label>
            <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={form.is_active} onCheckedChange={(c) => setForm({ ...form, is_active: c })} />
            <Label className="cursor-pointer">Active (include in pricing calculation)</Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
          <Button onClick={onSave} disabled={saving} data-testid="save-service-cost-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
            {editing ? 'Save' : 'Add'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </div>
);

export default ServiceCostsSection;
